import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useWindowSize } from 'react-use'

import { API } from '@/api'
import MenuIcon from '@/assets/icons/menu.svg?react'
import { message } from '@/components'
import { Loading, Trans } from '@/components'
import { CollapseTableRow } from '@/components/CollapseTableRow'
import { EmptyView } from '@/components/EmptyView.tsx'
import { Deployment } from '@/models/deployment.ts'
import { DeploymentBill } from '@/models/deploymentBill.ts'
import { InstanceStatus, RunningStatuses } from '@/models/instance.ts'
import DeploymentDetail from '@/pages/auto-deploy/components/DeploymentDetail.tsx'
import { formatPrice } from '@/utils/formatter.ts'
import { cn } from '@/utils/style.ts'

interface AutoDeploymentTableProps {
  _deploymentList: Deployment[]
  _deploymentBills: Map<string, DeploymentBill[]>
  _fetchDeploymentList: (page: number, showLoading: boolean) => void
  _status?: string
  _loading?: boolean
}

export default function AutoDeploymentTable({
  _deploymentList,
  _deploymentBills,
  _status,
  _loading,
  _fetchDeploymentList,
}: AutoDeploymentTableProps) {
  const { width: windowWidth } = useWindowSize()
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Memoize instance status map for all deployments
  const instanceStatusMap = useMemo(() => {
    const map = new Map<string, number>()
    _deploymentList.forEach((deploy) => {
      const bills = _deploymentBills.get(deploy.resID) || []
      map.set(deploy.resID, bills.length > 0 ? bills[0].instStatus : -1)
    })
    return map
  }, [_deploymentList, _deploymentBills])

  // Memoize running status map for all deployments
  const runningStatusMap = useMemo(() => {
    const map = new Map<string, boolean>()
    _deploymentList.forEach((deploy) => {
      const status = instanceStatusMap.get(deploy.resID) || -1
      map.set(deploy.resID, RunningStatuses.includes(status))
    })
    return map
  }, [_deploymentList, instanceStatusMap])

  // Memoize last running time map for all deployments
  const lastRunningTimeMap = useMemo(() => {
    const map = new Map<string, string>()
    _deploymentList.forEach((deploy) => {
      const bills = _deploymentBills.get(deploy.resID) || []
      map.set(
        deploy.resID,
        bills.length > 0
          ? dayjs.unix(bills[0].createTime.seconds).format('YYYY-MM-DD HH:mm:ss')
          : '-',
      )
    })
    return map
  }, [_deploymentList, _deploymentBills])

  const renderInstancesStatus = useCallback(
    (resID: string) => {
      const instanceStatus = instanceStatusMap.get(resID) || -1

      const statusConfig = new Map<number, { label: React.ReactNode; color: string }>([
        [
          InstanceStatus.Canceling,
          {
            label: <Trans i18nKey="deploy.instanceStatus.idle">Idle</Trans>,
            color: 'text-[#FF8447]',
          },
        ],
        [
          InstanceStatus.Pending,
          {
            label: <Trans i18nKey="deploy.instanceStatus.idle">Idle</Trans>,
            color: 'text-[#FF8447]',
          },
        ],
        [
          InstanceStatus.Running,
          {
            label: <Trans i18nKey="deploy.instanceStatus.running">Running</Trans>,
            color: 'text-[#1FC16B]',
          },
        ],
        [
          InstanceStatus.Exited,
          {
            label: <Trans i18nKey="deploy.instanceStatus.standby">Standby</Trans>,
            color: 'text-[#FF8447]',
          },
        ],
      ])

      const defaultStatus = {
        label: <Trans i18nKey="deploy.instanceStatus.standby">Standby</Trans>,
        color: 'text-[#FF8447]',
      }
      const { label, color } = statusConfig.get(instanceStatus) || defaultStatus
      return <div className={color}>{label}</div>
    },
    [instanceStatusMap],
  )

  const getLastRunningTime = useCallback(
    (resID: string): string => {
      return lastRunningTimeMap.get(resID) || '-'
    },
    [lastRunningTimeMap],
  )

  const isInstanceRunning = useCallback(
    (resID: string): boolean => {
      return runningStatusMap.get(resID) || false
    },
    [runningStatusMap],
  )

  const toggleDeployment = useCallback(
    (deployment: Deployment, enabled: boolean) => {
      API.updateDeployment({
        res_id: deployment.resID,
        gtw_res_id: deployment.gateway.resID,
        enabled,
        description: deployment.description,
        hardware_resource: JSON.stringify(deployment.runtimeEnv.hardwareEnv.resource),
        name: deployment.name,
        port: deployment.gateway.port,
        system_env: JSON.stringify(deployment.runtimeEnv.systemEnv),
      }).then((res) => {
        if (res.code === 0) {
          message.success(
            enabled
              ? t('deploy.message.resumeDeploySuccess')
              : t('deploy.message.suspendDeploySuccess'),
          )
          _fetchDeploymentList(1, false)
        }
      })
    },
    [_fetchDeploymentList],
  )

  const handleInstanceStatus = useCallback(
    (deployment: Deployment) => {
      const bills = _deploymentBills.get(deployment.resID) || []
      const running = runningStatusMap.get(deployment.resID) || false

      if (!running) {
        fetch(deployment.gateway.url, { mode: 'no-cors' })
        message.success(t('deploy.message.startDeploy'))
      } else {
        API.deleteInstance({
          res_id: bills[0].instResID,
        }).then((res) => {
          if (res.code === 0) {
            message.success(t('deploy.message.releaseInstance'))
            _fetchDeploymentList(1, false)
          }
        })
      }
    },
    [_deploymentBills, runningStatusMap, _fetchDeploymentList],
  )

  const handleDelete = useCallback(
    (resID: string) => {
      API.deleteDeployment({
        res_id: resID,
      })
        .then((res) => {
          if (res.code === 0) {
            message.success(t('deploy.message.deleteSuccess'))
            _fetchDeploymentList(1, false)
          }
        })
        .catch((err) => {
          console.error(err)
          message.error(t('deploy.message.deleteFailed'))
        })
    },
    [_fetchDeploymentList],
  )

  return (
    <>
      {_loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!_loading && _deploymentList.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}

      {!_loading && _deploymentList.length > 0 && (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={2}>
                <Trans i18nKey="deploy.tableHeaderID">ID</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="deploy.tableHeaderName">Name</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="deploy.tableHeaderStatus">Status</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="deploy.tableHeaderInsStatus">Instance Status</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="deploy.tableHeaderCost">Cost</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="deploy.tableHeaderLastRunTime">Last Running Time</Trans>
              </th>
              <th colSpan={windowWidth <= 1400 ? 1 : 3}>
                <Trans i18nKey="deploy.tableHeaderAction">Action</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {_deploymentList.map((deploy) => (
              <CollapseTableRow
                key={deploy.resID}
                colSpan={windowWidth <= 1400 ? 14 : 16}
                tableClassName={'!border-none ring-1 ring-[#E1E4EA] rounded-xl'}
                content={
                  <DeploymentDetail _deployment={deploy} _deploymentBills={_deploymentBills} />
                }
              >
                <td colSpan={2} className="t-sm">
                  {deploy.resID}
                </td>
                <td colSpan={2} className="t-sm">
                  {deploy.name}
                </td>
                <td colSpan={2} className="t-sm">
                  <div
                    className={cn(
                      't-s-sb text-primary',
                      _status === 'Activated' ? 'text-active' : 'text-error',
                    )}
                  >
                    {_status === 'Activated' ? (
                      <Trans i18nKey="deploy.status.activated">Activated</Trans>
                    ) : (
                      <Trans i18nKey="deploy.status.suspended">Suspended</Trans>
                    )}
                  </div>
                </td>
                <td colSpan={2} className="t-sm">
                  {renderInstancesStatus(deploy.resID)}
                </td>
                <td colSpan={2} className="t-sm">
                  {formatPrice(Math.abs(deploy.totalCostCent))}
                </td>
                <td colSpan={2} className="t-sm">
                  {getLastRunningTime(deploy.resID)}
                </td>
                <td colSpan={windowWidth <= 1400 ? 1 : 3} className="t-sm">
                  <div className="block 2xl:hidden">
                    <div
                      className="flex space-x-4 3xl:space-x-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="text-highlight cursor-pointer"
                        onClick={() => {
                          navigate('/deploy/create', {
                            state: {
                              editingDeployment: deploy,
                              isEditingDeployEnabled: _status === 'Activated',
                            },
                          })
                        }}
                      >
                        <Trans i18nKey="deploy.action.edit">Edit</Trans>
                      </div>
                      <div
                        className="text-highlight cursor-pointer"
                        onClick={() => {
                          toggleDeployment(deploy, _status !== 'Activated')
                        }}
                      >
                        {_status === 'Activated' ? (
                          <Trans i18nKey="deploy.action.suspend">Suspend</Trans>
                        ) : (
                          <Trans i18nKey="deploy.action.resume">Resume</Trans>
                        )}
                      </div>
                      <div
                        className={cn('cursor-pointer', {
                          'text-highlight': _status === 'Activated',
                          'text-tertiary': _status !== 'Activated',
                        })}
                        onClick={() => {
                          if (_status !== 'Activated') return
                          handleInstanceStatus(deploy)
                        }}
                      >
                        {isInstanceRunning(deploy.resID) ? (
                          <Trans i18nKey="deploy.action.release">Release</Trans>
                        ) : (
                          <Trans i18nKey="deploy.action.deploy">Deploy</Trans>
                        )}
                      </div>
                      <div
                        className="text-error cursor-pointer"
                        onClick={() => handleDelete(deploy.resID)}
                      >
                        <Trans i18nKey="deploy.action.delete">Delete</Trans>
                      </div>
                    </div>
                  </div>

                  <div className="relative group hidden 2xl:block">
                    <MenuIcon
                      className="cursor-pointer w-4 h-4 peer"
                      onClick={(e) => e.stopPropagation()}
                    />

                    <div className="absolute left-1/2 top-4 -translate-x-1/2 hidden peer-hover:block group-hover:block pt-2 z-50">
                      <div
                        className="p-1 shadow rounded-[6px] bg-white"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="cursor-pointer">
                          <div
                            className="px-3 py-2 hover:bg-[#C0D5FF] rounded"
                            onClick={() => {
                              navigate('/deploy/create', {
                                state: {
                                  editingDeployment: deploy,
                                  isEditingDeployEnabled: _status === 'Activated',
                                },
                              })
                            }}
                          >
                            <Trans i18nKey="deploy.action.edit">Edit</Trans>
                          </div>
                          <div
                            className="px-3 py-2 hover:bg-[#C0D5FF] rounded"
                            onClick={() => toggleDeployment(deploy, _status !== 'Activated')}
                          >
                            {_status === 'Activated' ? (
                              <Trans i18nKey="deploy.action.suspend">Suspend</Trans>
                            ) : (
                              <Trans i18nKey="deploy.action.resume">Resume</Trans>
                            )}
                          </div>
                          <div
                            className="px-3 py-2 hover:bg-[#C0D5FF] rounded"
                            onClick={() => handleInstanceStatus(deploy)}
                          >
                            {isInstanceRunning(deploy.resID) ? (
                              <Trans i18nKey="deploy.action.release">Release</Trans>
                            ) : (
                              <Trans i18nKey="deploy.action.deploy">Deploy</Trans>
                            )}
                          </div>
                          <div
                            className="px-3 py-2 text-error hover:bg-[#C0D5FF] rounded"
                            onClick={() => handleDelete(deploy.resID)}
                          >
                            <Trans i18nKey="deploy.action.delete">Delete</Trans>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </CollapseTableRow>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}
